import type { EditableZoneKind } from "../../../../api/rest";
import {
  type EditableZone, type Ring, mintZoneId, nearestEdge, nearestVertex, pointInPolygon,
  rectToRing, ringValid, snap, translateRing, zoneEquals,
} from "./zoneGeometry";

export type Tool = "select" | "rect" | "polygon";
type P = [number, number];
export type Drag =
  | { type: "move"; id: string; start: P; before: Ring }
  | { type: "vertex"; id: string; index: number; before: Ring }
  | { type: "rect"; start: P; current: P };

export interface EditorState {
  original: EditableZone[];
  working: EditableZone[];
  selectedId: string | null;
  tool: Tool;
  snapOn: boolean;
  lastKind: EditableZoneKind;
  drag: Drag | null;
  /** Polygon under construction (world metres). */
  pending: Ring | null;
}

export type Action =
  | { type: "load"; zones: EditableZone[] }
  | { type: "setTool"; tool: Tool }
  | { type: "toggleSnap" }
  | { type: "select"; id: string | null }
  | { type: "pointerDown"; p: P; tolM: number }
  | { type: "pointerMove"; p: P }
  | { type: "pointerUp" }
  | { type: "altClick"; p: P; tolM: number }
  | { type: "closePolygon" }
  | { type: "cancel" }
  | { type: "deleteSelected" }
  | { type: "setKind"; kind: EditableZoneKind }
  | { type: "setMaxSpeed"; v: number | null }
  | { type: "setDirection"; v: number | null }
  | { type: "discard" }
  | { type: "saved" };

export const initialState: EditorState = {
  original: [], working: [], selectedId: null, tool: "select", snapOn: true,
  lastKind: "staging", drag: null, pending: null,
};

export function isDirty(s: EditorState): boolean {
  if (s.original.length !== s.working.length) return true;
  const byId = new Map(s.original.map((z) => [z.id, z]));
  return s.working.some((w) => { const o = byId.get(w.id); return !o || !zoneEquals(o, w); });
}

const snapP = (p: P, on: boolean): P => [snap(p[0], on), snap(p[1], on)];
/** Reverts an in-flight move/vertex drag to the ring captured at pointerDown. */
function revertDrag(s: EditorState): EditorState {
  const d = s.drag;
  if (!d || d.type === "rect") return s;
  return { ...s, working: s.working.map((z) => (z.id === d.id ? { ...z, polygon: d.before.map(([x, y]) => [x, y]) } : z)) };
}

const clone = (zs: EditableZone[]) => zs.map((z) => ({ ...z, polygon: z.polygon.map(([x, y]) => [x, y]) }));

function patchSelected(s: EditorState, f: (z: EditableZone) => EditableZone): EditorState {
  if (!s.selectedId) return s;
  return { ...s, working: s.working.map((z) => (z.id === s.selectedId ? f(z) : z)) };
}

/** A ring the server would refuse (crossing, < 3 distinct points, no area) creates nothing. */
function addZone(s: EditorState, polygon: Ring): EditorState {
  if (!ringValid(polygon)) return { ...s, drag: null, pending: null, tool: "select" };
  const id = mintZoneId(s.lastKind, new Set(s.working.map((z) => z.id)));
  const z: EditableZone = { id, kind: s.lastKind, polygon, maxSpeedMps: null, directionRad: null };
  return { ...s, working: [...s.working, z], selectedId: id, drag: null, pending: null, tool: "select" };
}

export function reduce(s: EditorState, a: Action): EditorState {
  switch (a.type) {
    case "load":
      return { ...s, original: clone(a.zones), working: clone(a.zones), selectedId: null, drag: null, pending: null };
    case "setTool":
      return { ...revertDrag(s), tool: a.tool, drag: null, pending: null };
    case "toggleSnap":
      return { ...s, snapOn: !s.snapOn };
    case "select":
      return { ...s, selectedId: a.id };

    case "pointerDown": {
      const p = snapP(a.p, s.snapOn);
      if (s.tool === "rect") return { ...s, drag: { type: "rect", start: p, current: p } };
      if (s.tool === "polygon") {
        const pending = s.pending ?? [];
        if (pending.length >= 3 && nearestVertex(a.p, [pending[0]], a.tolM) === 0) return addZone(s, pending);
        return { ...s, pending: [...pending, p] };
      }
      // select tool — handle first, then body
      const sel = s.working.find((z) => z.id === s.selectedId);
      if (sel) {
        const vi = nearestVertex(a.p, sel.polygon, a.tolM);
        if (vi != null) return { ...s, drag: { type: "vertex", id: sel.id, index: vi, before: sel.polygon } };
      }
      const hit = [...s.working].reverse().find((z) => pointInPolygon(a.p, z.polygon));
      if (!hit) return { ...s, selectedId: null, drag: null };
      return { ...s, selectedId: hit.id, drag: { type: "move", id: hit.id, start: a.p, before: hit.polygon } };
    }

    case "pointerMove": {
      const d = s.drag;
      if (!d) return s;
      const p = snapP(a.p, s.snapOn);
      if (d.type === "rect") return { ...s, drag: { ...d, current: p } };
      if (d.type === "vertex") {
        return { ...s, working: s.working.map((z) => z.id === d.id
          ? { ...z, polygon: d.before.map((q, i) => (i === d.index ? [p[0], p[1]] : q)) } : z) };
      }
      const dx = snap(a.p[0] - d.start[0], s.snapOn), dy = snap(a.p[1] - d.start[1], s.snapOn);
      return { ...s, working: s.working.map((z) => (z.id === d.id ? { ...z, polygon: translateRing(d.before, dx, dy) } : z)) };
    }

    case "pointerUp": {
      const d = s.drag;
      if (!d) return s;
      if (d.type === "rect") return addZone(s, rectToRing(d.start, d.current));
      // A drag that leaves the ring invalid (e.g. a vertex dragged across an edge) is undone, as Esc would.
      const z = s.working.find((w) => w.id === d.id);
      if (z && !ringValid(z.polygon)) return { ...revertDrag(s), drag: null };
      return { ...s, drag: null };
    }

    case "altClick": {
      const sel = s.working.find((z) => z.id === s.selectedId);
      if (!sel) return s;
      const vi = nearestVertex(a.p, sel.polygon, a.tolM);
      if (vi != null) {
        if (sel.polygon.length <= 3) return s;
        const removed = sel.polygon.filter((_, i) => i !== vi);
        return ringValid(removed) ? patchSelected(s, (z) => ({ ...z, polygon: removed })) : s;
      }
      const e = nearestEdge(a.p, sel.polygon, a.tolM);
      if (!e) return s;
      const q = snapP(e.point, s.snapOn);
      // Snapping can pull the inserted vertex off the edge and across another one.
      const inserted = [...sel.polygon.slice(0, e.index + 1), [q[0], q[1]], ...sel.polygon.slice(e.index + 1)];
      return ringValid(inserted) ? patchSelected(s, (z) => ({ ...z, polygon: inserted })) : s;
    }

    case "closePolygon":
      return s.pending && s.pending.length >= 3 ? addZone(s, s.pending) : s;
    case "cancel":
      if (s.drag) return { ...revertDrag(s), drag: null };
      if (s.pending) return { ...s, pending: null };
      return { ...s, selectedId: null };

    case "deleteSelected":
      if (!s.selectedId) return s;
      return { ...s, working: s.working.filter((z) => z.id !== s.selectedId), selectedId: null, drag: null };

    case "setKind":
      if (!s.selectedId) return s;
      return { ...patchSelected(s, (z) => ({ ...z, kind: a.kind })), lastKind: a.kind };
    case "setMaxSpeed":
      return patchSelected(s, (z) => ({ ...z, maxSpeedMps: a.v }));
    case "setDirection":
      return patchSelected(s, (z) => ({ ...z, directionRad: a.v }));

    case "discard":
      return { ...s, working: clone(s.original), selectedId: null, drag: null, pending: null };
    case "saved":
      return { ...s, original: clone(s.working) };
  }
}
